
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import BreakpointsSelection from "@/components/Builder/TopBar/BreakpointsSelection.vue";
import {
  BASE_BREAKPOINT_ID,
  DEFAULT_MINIMUM_BREAKPOINT_WIDTH,
  getMinimumWidth
} from "@/lib/breakpoints/breakpoints";
import IconButton from "@/components/UI/IconButton.vue";
import TopBarSection from "@/components/Reusable/AppLayout/TopBar/TopBarSection.vue";
import fpJsonParseTypesafe from "@/util/fp-json-parse-typesafe";
import { pipe } from "fp-ts/function";
import { fold } from "fp-ts/Either";
import { TreeNode, TreeNodeV } from "@/types/tree";
import DesignLibrarySkeleton from "@/components/Add/library/DesignLibrarySkeleton.vue";

import * as t from "io-ts";
import NiceButton from "@/components/UI/NiceButton.vue";
import {
  getLastUsedDesignProviderUrl,
  pasteElements,
  setLastUsedDesignProviderUrl
} from "@/lib/design-library/design-library";
import { configStore, globalStore, uiStore } from "@/store/store-accessor";
import { smoothScrollToLastElement } from "@/lib/canvas/scroll-to-active-element";
import DesignLibrarySidebar from "@/components/Add/library/DesignLibrarySidebar.vue";
import DesignLibraryGlobalSettings from "@/components/Add/library/DesignLibraryGlobalSettings.vue";
import {
  DesignLibraryDataV,
  DesignSetData,
  DesignSetDataV,
  DesignSetPost
} from "@/types/design-library";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import { keyboardShortcuts } from "@/lib/keyboard-shortcuts/keyboard-shortcuts";
import { KEYBOARD_SHORTCUTS } from "@/lib/keyboard-shortcuts/global-shortcuts";
import DesignProviderDropdown from "@/components/Add/library/DesignProviderDropdown.vue";
import { DesignProvider } from "@/types/design-library";
import ProOnlyAlertBox from "@/components/Subscription/ProOnlyAlertBox.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import DesignLibraryDesignPresets from "./DesignLibraryDesignPresets.vue";
import DesignLibraryPassword from "@/components/Add/library/DesignLibraryPassword.vue";
import { logEvent } from "@/util/tracking";

@Component({
  components: {
    ProOnlyAlertBox,
    IconButton,
    NiceButton,
    DesignLibrarySidebar,
    DesignLibrarySkeleton,
    TopBarSection,
    TextInput,
    DropdownInput,
    BreakpointsSelection,
    DesignProviderDropdown,
    DesignLibraryPassword,
    DesignLibraryGlobalSettings,
    DesignLibraryDesignPresets
  }
})
export default class DesignLibraryModal extends Vue {
  selectedProvider: DesignProvider | null = null;
  selectedPostId: number | null = null;
  providerElements: TreeNode[] = [];
  reliesOnGlobalSettings: DesignSetData["reliesOnGlobalSettings"] = false;
  reliesOnDesignPresets: DesignSetData["reliesOnDesignPresets"] = false;
  providerPosts: DesignSetPost[] = [];
  importingPage = false;

  requiresPassword = false;
  passwordValid = true;

  breakpoint = {
    id: BASE_BREAKPOINT_ID,
    label: "",
    minWidth: undefined,
    maxWidth: undefined,
    defaultPreviewWidth: DEFAULT_MINIMUM_BREAKPOINT_WIDTH
  };

  canvasSize = {
    width: 0,
    height: 0
  };

  loading = false;
  previewLoaded = false;
  fullscreen = false;
  loadingDesignSetData = false;

  mounted() {
    if (!uiStore.hasFullAccess) return;
    keyboardShortcuts.register(KEYBOARD_SHORTCUTS.CMD_B, this.open);
    this.setSelectedProviderToLastUsedProvider();
  }

  beforeDestroy() {
    keyboardShortcuts.unbind(KEYBOARD_SHORTCUTS.CMD_B);

    this.unbindListeners();
  }

  get library() {
    return configStore.designLibrary;
  }

  get providers() {
    return this.library.designProviders;
  }

  get showPasswordForm() {
    return this.requiresPassword && !this.passwordValid;
  }

  setSelectedProviderToLastUsedProvider() {
    const providerUrl = getLastUsedDesignProviderUrl();
    const foundProvider = this.providers.find(
      provider => provider.url === providerUrl
    );

    if (foundProvider) {
      this.selectedProvider = foundProvider;
    } else {
      const defaultProvider = this.providers.find(
        provider => provider.name === "This Website"
      );

      if (defaultProvider) {
        this.selectedProvider = defaultProvider;
      }
    }
  }

  get sidebarItems() {
    return this.providerPosts; // TODO: Support nesting?
  }

  get isOpen() {
    return uiStore.panelState.designLibraryModalOpen;
  }

  set isOpen(event) {
    uiStore.setDesignLibraryModalState(event);
  }

  get selectedPost() {
    return this.providerPosts.find(post => post.id === this.selectedPostId);
  }

  get selectedUrl() {
    return this.selectedPost?.url || this.selectedProvider?.url;
  }

  get iframeUrl() {
    if (!this.selectedUrl) return null;

    const iframeUrl = new URL(this.selectedUrl);
    iframeUrl.searchParams.append("breakdance", "design-library");

    if (this.currentProviderPassword) {
      iframeUrl.searchParams.set("password", this.currentProviderPassword);
    }

    return iframeUrl.toString();
  }

  get previewWidth() {
    if (this.breakpoint.id === BASE_BREAKPOINT_ID) return "100%";
    return this.breakpoint.defaultPreviewWidth;
  }

  get minWidth() {
    return getMinimumWidth(globalStore);
  }

  get iframeWrapperStyles() {
    return {
      width: this.previewWidth === "100%" ? "100%" : `${this.previewWidth}px`
    };
  }

  get iframeStyles() {
    const scaledStyles = (width: number) => {
      const scaleFactor = Math.min(1, this.canvasSize.width / width);
      return {
        width: `${width}px`,
        transform: `scale(${scaleFactor})`,
        height: `${this.canvasSize.height * (1 / scaleFactor)}px`
      };
    };

    if (this.previewWidth === "100%") {
      if (this.canvasSize.width < this.minWidth) {
        return scaledStyles(this.minWidth);
      } else {
        return {
          width: "100%",
          minWidth: `${this.minWidth}px`
        };
      }
    } else {
      return scaledStyles(this.previewWidth);
    }
  }

  get canImportAll() {
    return this.providerElements.length;
  }

  get currentProviderPassword() {
    const url = new URL(this.selectedProvider?.url || "");
    return url.searchParams.get("password");
  }

  @Watch("isOpen")
  onDialogOpen() {
    if (this.isOpen) {
      this.modalOpened();
    } else {
      this.unbindListeners();
    }
  }

  @Watch("selectedProvider")
  onProviderChange(
    newProvider: DesignProvider,
    oldProvider: DesignProvider | null
  ) {
    // Reset state
    this.providerElements = [];
    this.selectedPostId = null;
    this.providerPosts = [];
    this.reliesOnGlobalSettings = false;
    this.reliesOnDesignPresets = false;

    this.getDesignSetData();

    if (newProvider.url !== oldProvider?.url) {
      this.previewLoaded = false;
    }
  }

  modalOpened() {
    if (!this.selectedProvider) {
      this.selectedProvider = this.providers[1];
    }

    logEvent("Design Library", {
      action: "modal_opened"
    });

    // Calculate canvas size only after the dialog is fully open.
    this.calculateSizeAfterTransition();

    window.addEventListener("message", this.handleIframeMessage, false);
    window.addEventListener("resize", this.setCanvasSize);
  }

  unbindListeners() {
    window.removeEventListener("message", this.handleIframeMessage, false);
    window.removeEventListener("resize", this.setCanvasSize);
  }

  calculateSizeAfterTransition() {
    const transitionDuration = 400;
    setTimeout(() => this.setCanvasSize(), transitionDuration);
  }

  setCanvasSize() {
    const wrapper = this.$refs.iframeWrapper as HTMLElement;
    if (!wrapper) return;

    const { width, height } = wrapper.getBoundingClientRect();
    this.canvasSize = { width, height };
  }

  reloadIframe() {
    const iframe = this.$refs.iframe as HTMLIFrameElement;

    // https://github.com/soflyy/breakdance/pull/4317/files#r986884610
    // fixes an error on dev when it rehydrates state
    if (!iframe) {
      this.selectedProvider = this.providers[0];

      return;
    }

    iframe.src += "";
    this.loading = true;
  }

  goHome() {
    this.selectedPostId = null;
    this.reloadIframe();
  }

  toggleFullscreen() {
    this.fullscreen = !this.fullscreen;
    this.calculateSizeAfterTransition();
  }

  handleIframeMessage(event: MessageEvent) {
    if (typeof event.data !== "string") return;

    const [name, data] = event.data.split(/,(.*)/s);

    if (name === "breakdanceGetElements") {
      this.getElements(data);
    }

    if (name === "breakdanceImportElement") {
      this.importElement(data);
    }

    if (name === "breakdanceBeforeLeave") {
      this.navigatedAway();
    }
  }

  onPostSelected(posts: number[]) {
    const [selectedId] = posts;
    this.loading = true;
    this.selectedPostId = selectedId || null;
    this.reloadIframe();

    logEvent("Design Library", {
      action: "modal_page_selected",
      provider: this.selectedProvider?.name,
      post_id: selectedId
    });
  }

  onProviderLoaded() {
    this.previewLoaded = true;
    this.loading = false;
    this.setCanvasSize();
  }

  getElements(elementsAsString: string) {
    this.loading = false;

    const maybeElements = fpJsonParseTypesafe(
      elementsAsString,
      t.array(TreeNodeV)
    );

    this.providerElements = pipe(
      maybeElements,
      fold(
        () => [],
        els => els
      )
    );
  }

  navigatedAway() {
    this.loading = true;
  }

  importElement(elementAsString: string) {
    const maybeElement = fpJsonParseTypesafe(elementAsString, TreeNodeV);

    const element = pipe(
      maybeElement,
      fold(
        () => null,
        el => el
      )
    );

    if (!element) return;

    logEvent("Design Library", {
      action: "modal_import_element",
      provider: this.selectedProvider?.name,
      elementSlug: element.data.type
    });

    this.insertElements([element]);
    this.close();

    this.$nextTick(() => {
      smoothScrollToLastElement();
    });
  }

  insertElements(elements: TreeNode[]) {
    pasteElements(elements);
    if (this.selectedProvider) {
      setLastUsedDesignProviderUrl(this.selectedProvider.url);
    }
  }

  importAll() {
    this.importingPage = true;
    this.insertElements(this.providerElements);

    this.close();
    this.importingPage = false;

    this.$nextTick(() => {
      smoothScrollToLastElement();
    });
  }

  close() {
    uiStore.setDesignLibraryModalState(false);
  }

  open() {
    uiStore.setDesignLibraryModalState(true);
  }

  async getDesignSetData() {
    const url = this.selectedProvider?.url;
    this.loadingDesignSetData = true;

    pipe(
      await makeWordPressAjaxRequest({
        action: this.selectedProvider?.isLocal
          ? "breakdance_get_local_design_set"
          : "breakdance_get_external_design_set",
        decoder: DesignSetDataV,
        friendlyName: "Design Set Data",
        dataToPost: { url }
      }),
      fold(
        () => {
          this.providerPosts = [];
        },
        designSetData => {
          this.providerPosts = designSetData.posts;
          this.requiresPassword = designSetData.requiresPassword || false;
          this.passwordValid = !!designSetData.passwordValid;
          this.reliesOnGlobalSettings = designSetData.reliesOnGlobalSettings;
          this.reliesOnDesignPresets = designSetData.reliesOnDesignPresets;
        }
      )
    );

    this.loadingDesignSetData = false;
  }

  async getAllProvidersAndReloadSelected() {
    await this.getUpdatedDesignProviders();

    const foundProvider = this.providers.find(
      provider => provider.name === this.selectedProvider?.name
    );

    this.selectedProvider = foundProvider || this.providers[0];
  }

  async getUpdatedDesignProviders() {
    pipe(
      await makeWordPressAjaxRequest({
        action: "breakdance_get_design_library_data",
        decoder: DesignLibraryDataV
      }),
      fold(
        () => {},
        data => {
          configStore.setDesignLibrary(data);
        }
      )
    );
  }
}
